import type { GapDraftView } from '@momentum/types';

import { ENRICHMENT_LABEL } from './agents';

/**
 * Whether a draft could become a published `Gap`, field by field.
 *
 * `Gap` requires four things a draft does not: `externalRef`, `title`, a
 * complete `projectOverview` and a numeric 0–1 `severity`. The checklist exists
 * so the disabled publish control can say WHICH of them is missing rather than
 * being hidden — see the final review view.
 *
 * Every check reads `gap.*` and reports what it finds, `severity` included. The
 * mockup hardcoded that one to `false` because nothing wrote it; the enrichment
 * write script derives it from the run's impact level, so it is evaluated like
 * the rest rather than assumed missing.
 */
export interface ReadinessCheck {
  /** The `Gap` field name, verbatim — this list is a contract, not prose. */
  label: string;
  ok: boolean;
  /** The value when present, or why it is absent when it is not. */
  detail: string;
}

/** How many `Gap` fields a draft has to satisfy before it could be published. */
export const REQUIRED_GAP_FIELD_COUNT = 4;

export function readinessChecks(draft: GapDraftView): ReadinessCheck[] {
  const { externalRef, title, projectOverview, severity } = draft.gap;

  // The lede is one required field, not three: the schema takes `projectOverview`
  // whole, and a partial one is as unpublishable as none at all.
  const ledeGaps = (
    [
      ['account', projectOverview?.account],
      ['project', projectOverview?.project],
      ['gapSummary', projectOverview?.gapSummary],
    ] as const
  )
    .filter(([, value]) => value === undefined || value.length === 0)
    .map(([fieldName]) => fieldName);

  return [
    {
      label: 'externalRef',
      ok: externalRef !== undefined,
      detail:
        externalRef === undefined
          ? 'absent'
          : `${externalRef.entityType} · ${externalRef.externalId}`,
    },
    {
      label: 'title',
      ok: title !== undefined && title.length > 0,
      detail:
        title === undefined || title.length === 0
          ? 'falls back to source.title, or is derived from the claim'
          : title,
    },
    {
      label: 'projectOverview (account · project · gapSummary)',
      ok: ledeGaps.length === 0,
      detail:
        ledeGaps.length === 0
          ? `written by ${ENRICHMENT_LABEL}`
          : `missing ${ledeGaps.join(', ')}`,
    },
    {
      label: 'severity (0–1)',
      ok: typeof severity === 'number',
      detail:
        typeof severity === 'number'
          ? severity.toFixed(2)
          : `owed by ${ENRICHMENT_LABEL}`,
    },
  ];
}

/** How many of the four required fields this draft can satisfy today. */
export function passedCheckCount(checks: ReadinessCheck[]): number {
  return checks.filter((check) => check.ok).length;
}
